const getAdminBaseUrl = () => {
  const rawBase =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.minezeta.com/admin";
  const trimmed = rawBase.replace(/\/+$/, "");
  return trimmed.endsWith("/admin") ? trimmed : `${trimmed}/admin`;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") || localStorage.getItem("adminToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

/**
 * Fetch unified coin history for a specific user
 * @param {string|number} userId
 * @param {Object} params
 * @param {number} [params.page=1]
 * @param {number} [params.perPage=20]
 * @param {string} [params.source="all"] - "all" | "mining" | "browser" | "event" | "points" | "event_point" | "ban"
 * @param {string} [params.type] - "CREDIT" | "DEBIT"
 */
export const getUserCoinHistory = async (
  userId,
  { page = 1, perPage = 20, source = "all", type = "" } = {}
) => {
  const baseUrl = getAdminBaseUrl();
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) {
    throw new Error("Invalid User ID");
  }

  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", String(page));
  if (perPage) queryParams.append("perPage", String(perPage));
  if (source && source !== "all") queryParams.append("source", source);
  else queryParams.append("source", "all");
  if (type && type !== "all") queryParams.append("type", type);

  const queryString = `?${queryParams.toString()}`;

  // Try /users/:userId/coin-history first
  let primaryUrl = `${baseUrl}/users/${encodeURIComponent(cleanUserId)}/coin-history${queryString}`;
  let response;

  try {
    response = await fetch(primaryUrl, {
      method: "GET",
      headers: getAuthHeaders(),
    });
  } catch (netErr) {
    throw new Error(netErr.message || "Network error connecting to API");
  }

  // Fallback to /user/:userId/coin-history if 404
  if (response.status === 404) {
    const fallbackUrl = `${baseUrl}/user/${encodeURIComponent(cleanUserId)}/coin-history${queryString}`;
    try {
      const fallbackRes = await fetch(fallbackUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (fallbackRes.ok || fallbackRes.status !== 404) {
        response = fallbackRes;
      }
    } catch {
      // ignore fallback error and continue with initial response handling
    }
  }

  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    console.error("User coin history API error response:", {
      status: response.status,
      result,
      userId: cleanUserId,
    });
    const errorMsg =
      (typeof result === "object" &&
        (result?.error?.message ||
          result?.error ||
          result?.sqlMessage ||
          result?.message ||
          result?.details)) ||
      (typeof result === "string" ? result : "Failed to fetch user coin history");
    throw new Error(errorMsg);
  }

  return result;
};

/**
 * Fetch global coin history ledger across network
 * @param {Object} params
 * @param {string} [params.search=""]
 * @param {string} [params.source="all"]
 * @param {string} [params.type=""]
 * @param {number} [params.page=1]
 * @param {number} [params.perPage=20]
 */
export const getGlobalCoinHistory = async ({
  search = "",
  source = "all",
  type = "",
  page = 1,
  perPage = 20,
} = {}) => {
  const baseUrl = getAdminBaseUrl();
  const queryParams = new URLSearchParams();
  if (page) queryParams.append("page", String(page));
  if (perPage) queryParams.append("perPage", String(perPage));
  if (search && search.trim()) queryParams.append("search", search.trim());
  if (source && source !== "all") queryParams.append("source", source);
  if (type && type !== "all") queryParams.append("type", type);

  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : "";
  let primaryUrl = `${baseUrl}/users/coin-history${queryString}`;
  let response;

  try {
    response = await fetch(primaryUrl, {
      method: "GET",
      headers: getAuthHeaders(),
    });
  } catch (netErr) {
    throw new Error(netErr.message || "Network error connecting to API");
  }

  // Fallback to /user/coin-history if 404
  if (response.status === 404) {
    const fallbackUrl = `${baseUrl}/user/coin-history${queryString}`;
    try {
      const fallbackRes = await fetch(fallbackUrl, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (fallbackRes.ok || fallbackRes.status !== 404) {
        response = fallbackRes;
      }
    } catch {
      // ignore
    }
  }

  const contentType = response.headers.get("content-type");
  const isJson = contentType && contentType.includes("application/json");
  const result = isJson ? await response.json() : await response.text();

  if (!response.ok || (typeof result === "object" && result.status === false)) {
    const errorMsg =
      (typeof result === "object" && (result?.message || result?.error || result?.details)) ||
      (typeof result === "string" ? result : "Failed to fetch global coin history");
    throw new Error(errorMsg);
  }

  return result;
};

export { getEventPointsLedger } from "./eventPointsApi";

